import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { DATA_REFRESH_EVENT } from '../../contexts/WebSocketContext';
import { 
  BookOpen, Clock, Award, TrendingUp, Play, Bot, Calendar, FileText, 
  Search, Bell, Video, ChevronRight, AlertCircle, CheckCircle2, ExternalLink, X
} from 'lucide-react';
import { toast } from 'sonner';

const StudentDashboard = ({ onMenuClick }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  // Map announcement_id -> { notifId, is_read } so we can hide read announcements
  const [announcementNotifs, setAnnouncementNotifs] = useState({});

  const fetchDashboardData = useCallback(async () => {
    try {
      const response = await api.get('/student/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch announcement read-state from notifications so the dashboard banner
  // disappears once the student has read the announcement in their notifications.
  const fetchAnnouncementReadState = useCallback(async () => {
    try {
      const response = await api.get('/notifications');
      const map = {};
      response.data.forEach((n) => {
        const annId = n.extra_data?.announcement_id;
        if (n.type === 'nouvelle_annonce' && annId) {
          map[annId] = { notifId: n.id, is_read: n.is_read };
        }
      });
      setAnnouncementNotifs(map);
    } catch (error) {
      // Non-blocking: if it fails we just show the announcement
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    fetchAnnouncementReadState();
  }, [fetchDashboardData, fetchAnnouncementReadState]);

  // Listen for real-time data refresh events
  useEffect(() => {
    const handleDataRefresh = (event) => {
      const { type } = event.detail;
      console.log('Student dashboard received data refresh:', type);
      
      // Refresh dashboard data when relevant events occur
      const refreshTypes = [
        'homework_created', 'homework_submitted', 'homework_graded',
        'live_session_created', 'evaluation_created', 'announcement_created'
      ];
      if (refreshTypes.includes(type)) {
        fetchDashboardData();
      }
    };

    window.addEventListener(DATA_REFRESH_EVENT, handleDataRefresh);
    return () => window.removeEventListener(DATA_REFRESH_EVENT, handleDataRefresh);
  }, [fetchDashboardData]);

  if (loading || !dashboardData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  const { stats, courses, upcoming_sessions, pending_homework, announcements, evaluations } = dashboardData;

  const statCards = [
    { title: 'Cours en cours', value: stats.courses_enrolled, icon: BookOpen, color: 'text-violet-600', bg: 'bg-violet-50 dark:bg-violet-900/20' },
    { title: 'Progression', value: `${stats.overall_progress}%`, icon: TrendingUp, color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
    { title: 'Note moyenne', value: `${stats.avg_grade}%`, icon: Award, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-900/20' },
    { title: 'Sessions à venir', value: stats.upcoming_sessions, icon: Video, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20' },
  ];

  const getPriorityBadge = (priority) => {
    switch(priority) {
      case 'urgent': return <Badge variant="destructive">Urgent</Badge>;
      case 'important': return <Badge className="bg-amber-100 text-amber-700">Important</Badge>;
      default: return null;
    }
  };

  // First announcement that hasn't been read yet in notifications
  const visibleAnnouncement = (announcements || []).find(
    (a) => !announcementNotifs[a.id]?.is_read
  );

  // Dismiss from the dashboard = mark the related notification as read
  const dismissAnnouncement = async (announcementId) => {
    const entry = announcementNotifs[announcementId];
    setAnnouncementNotifs((prev) => ({
      ...prev,
      [announcementId]: { ...(prev[announcementId] || {}), is_read: true }
    }));
    if (entry?.notifId) {
      try {
        await api.put(`/notifications/${entry.notifId}/read`);
        window.dispatchEvent(new CustomEvent('refreshBadges'));
      } catch (error) {
        // Ignore: UI already hid it
      }
    }
  };

  const getHomeworkStatus = (status) => {
    switch(status) {
      case 'graded': return { label: 'Corrigé', color: 'text-emerald-600 bg-emerald-50' };
      case 'submitted': return { label: 'Soumis', color: 'text-blue-600 bg-blue-50' };
      case 'overdue': return { label: 'En retard', color: 'text-red-600 bg-red-50' };
      default: return { label: 'À faire', color: 'text-amber-600 bg-amber-50' };
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Mon espace" onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-2">Bonjour, {user?.first_name} !</h2>
          <p className="text-slate-600 dark:text-slate-400">Prêt à continuer votre apprentissage ?</p>
        </div>

        {/* Announcements Alert */}
        {visibleAnnouncement && (
          <Card className="mb-6 border-l-4 border-l-amber-500 bg-amber-50/50 dark:bg-amber-900/10">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Bell className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-slate-900 dark:text-white">{visibleAnnouncement.title}</h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 line-clamp-3 whitespace-pre-wrap">{visibleAnnouncement.content}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <p className="text-xs text-slate-500">Par {visibleAnnouncement.author_name}</p>
                    <button
                      type="button"
                      onClick={() => navigate('/notifications')}
                      className="text-xs font-medium text-violet-600 hover:text-violet-700"
                    >
                      Lire l'annonce
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {getPriorityBadge(visibleAnnouncement.priority)}
                  <button
                    type="button"
                    onClick={() => dismissAnnouncement(visibleAnnouncement.id)}
                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                    title="Marquer comme lue"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Global Search Bar */}
        <Card className="mb-8">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Rechercher un cours, un devoir, un document..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 text-base"
                data-testid="global-search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="stat-card hover:shadow-lg transition-all" data-testid={`stat-card-${index}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-14 h-14 rounded-xl ${stat.bg} flex items-center justify-center`}>
                      <Icon className={`w-7 h-7 ${stat.color}`} strokeWidth={1.5} />
                    </div>
                    <span className="text-3xl font-bold text-slate-900 dark:text-white">{stat.value}</span>
                  </div>
                  <h3 className="text-sm text-slate-600 dark:text-slate-400">{stat.title}</h3>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Courses */}
          <div className="lg:col-span-2 space-y-6">
            {/* Upcoming Live Sessions */}
            <Card data-testid="upcoming-sessions-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Video className="w-5 h-5 text-emerald-600" />
                    Sessions Live à venir
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/live-classes')}>
                    Tout voir <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {upcoming_sessions.length === 0 ? (
                  <p className="text-slate-500 text-center py-6">Aucune session programmée</p>
                ) : (
                  <div className="space-y-3">
                    {upcoming_sessions.map((session) => (
                      <div key={session.id} className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200 dark:border-emerald-800" data-testid={`session-${session.id}`}>
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                          <Video className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-slate-900 dark:text-white truncate">{session.title}</h4>
                          <p className="text-sm text-slate-600 dark:text-slate-400">{session.course_name} • {session.trainer_name}</p>
                          <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                            <Calendar className="w-3 h-3" />
                            {session.scheduled_at && new Date(session.scheduled_at).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                        {session.live_url && (
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => window.open(session.live_url, '_blank')}>
                            <Play className="w-4 h-4 mr-1" /> Rejoindre
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pending Homework */}
            <Card data-testid="pending-homework-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-amber-600" />
                    Devoirs en attente
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/homework')}>
                    Tout voir <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {pending_homework.length === 0 ? (
                  <div className="text-center py-6">
                    <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-2" />
                    <p className="text-slate-500">Tous vos devoirs sont à jour !</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pending_homework.slice(0, 3).map((hw) => {
                      const statusInfo = getHomeworkStatus(hw.status);
                      return (
                        <div key={hw.id} className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700" data-testid={`homework-${hw.id}`}>
                          <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-amber-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-slate-900 dark:text-white truncate">{hw.title}</h4>
                            <p className="text-sm text-slate-500">{hw.course_name}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                            {hw.due_date && (
                              <p className="text-xs text-slate-500 mt-1">
                                {new Date(hw.due_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* My Courses */}
            <Card data-testid="courses-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-violet-600" />
                    Mes Cours
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/courses')}>
                    Tout voir <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {courses.slice(0, 3).map((course) => (
                    <div key={course.id} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 hover:shadow-md transition-all cursor-pointer" onClick={() => navigate(`/course/${course.id}`)} data-testid={`course-${course.id}`}>
                      <div className="flex items-center gap-4 mb-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                          <BookOpen className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-slate-900 dark:text-white truncate">{course.name}</h4>
                          <p className="text-sm text-slate-500">{course.lessons_completed}/{course.lessons_total} leçons</p>
                        </div>
                        <span className="text-lg font-bold text-violet-600">{course.progress}%</span>
                      </div>
                      <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full transition-all" style={{ width: `${course.progress}%` }}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Quick Actions & Evaluations */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Actions rapides</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-3">
                <Button variant="outline" className="h-auto py-4 flex flex-col items-center gap-2" onClick={() => navigate('/courses')}>
                  <BookOpen className="w-5 h-5 text-violet-600" />
                  <span className="text-xs">Mes Cours</span>
                </Button>
                <Button variant="outline" className="h-auto py-4 flex flex-col items-center gap-2" onClick={() => navigate('/homework')}>
                  <FileText className="w-5 h-5 text-amber-600" />
                  <span className="text-xs">Devoirs</span>
                </Button>
                <Button variant="outline" className="h-auto py-4 flex flex-col items-center gap-2" onClick={() => navigate('/planning')}>
                  <Calendar className="w-5 h-5 text-emerald-600" />
                  <span className="text-xs">Planning</span>
                </Button>
                <Button variant="outline" className="h-auto py-4 flex flex-col items-center gap-2" onClick={() => navigate('/ai-assistant')}>
                  <Bot className="w-5 h-5 text-blue-600" />
                  <span className="text-xs">Assistant IA</span>
                </Button>
              </CardContent>
            </Card>

            {/* Evaluations */}
            <Card data-testid="evaluations-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Award className="w-5 h-5 text-violet-600" />
                    Évaluations
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/evaluations')}>
                    Tout voir
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {evaluations.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">Aucune évaluation</p>
                ) : (
                  <div className="space-y-3">
                    {evaluations.slice(0, 4).map((evaluation) => (
                      <div key={evaluation.id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50" data-testid={`evaluation-${evaluation.id}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${evaluation.completed ? 'bg-emerald-100 text-emerald-600' : 'bg-violet-100 text-violet-600'}`}>
                          {evaluation.completed ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{evaluation.title}</p>
                          <p className="text-xs text-slate-500">{evaluation.course_name} • {evaluation.question_count} questions</p>
                        </div>
                        {evaluation.completed && evaluation.score !== null && (
                          <span className={`text-sm font-semibold ${evaluation.score >= evaluation.passing_score ? 'text-emerald-600' : 'text-red-600'}`}>
                            {evaluation.score}%
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default StudentDashboard;
